import React, {useCallback, useMemo, useState} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft, faTriangleExclamation, faCheck, faUpRightAndDownLeftFromCenter,
    faDownLeftAndUpRightToCenter
} from "@fortawesome/pro-solid-svg-icons";
import { faTemperatureFull, faPuzzle } from "@fortawesome/pro-light-svg-icons";
import {faChevronRight} from "@fortawesome/pro-regular-svg-icons";
import { useProjectFormHeadingLayoutEffect } from "@/pages/takeoff/context/project-heading/use-project-heading-context.ts";
import { ProjectHeadingState } from "@/pages/takeoff/context/project-heading/project-heading-context-props.ts";
import { Switch, Case } from "@/components/switch";

import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import useValidationContext from "@/pages/takeoff/layouts/validation/context/validation/use-validation-context.ts";
import Button from "@/components/buttons/button";
import ValidationBadge from "@/components/validation-badge";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";
import usePackValidationContext from "./context/use-pack-validation-context.ts";
import PackDetailsSlide from "./components/pack-details-slide.tsx";
import PackViewerContainer from "./components/pack-viewer-container.tsx";
import PackSelection from "./components/pack-selection.tsx";
import { ValidationMode } from "./types/validation-mode-type.ts";
import useProjectFormNavigationContext
    from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";
import Card from "@/components/card";
import Tooltip from "@/components/tooltip";
import useMeasureProjectActions from "../../../measures/hooks/use-measure-project-actions.tsx";
import ViewerContextProvider from "@/services/context/viewer/instance/viewer-context-provider.tsx";
import useValidationLoaders from "./hooks/use-validation-loaders.ts";
import useValidationTools from "./hooks/use-validation-tools.ts";
import ProjectMeasureProvider from "@/pages/takeoff/context/project-measure/project-measure-provider.tsx";
import PackToolbar from "@/pages/takeoff/layouts/validation/layouts/details/components/pack-toolbar.tsx";

interface ValidationDetailsProps {
    storeyId: string;
}

const ValidationDetailsComponent: React.FC<ValidationDetailsProps> = ({ storeyId }) => {

    const {setRoute, getParam} = useProjectFormNavigationContext();
    
    const mode = getParam<ValidationMode>('mode') || 'functional';

    const { isLoading, validationPacks, acceptPack, refusePack } = useValidationContext();
    const { sendValidations } = usePackValidationContext();
    
    const pack = validationPacks?.find(p => p.storeyId === storeyId || p.id === storeyId);

    const { setupLaunch, launchProdJob } = useMeasureProjectActions();

    const onBackClick = useCallback(() => {
        setRoute('measures');
    }, [setRoute]);

    const onSaveAndUpdateClick = useCallback(async () => {
        if (pack == null) return;

        await sendValidations();
        setupLaunch("PDF Superposes - Update Package 1&2", true, () => setRoute('measures'));
        launchProdJob();
    }, [pack, sendValidations, setRoute, setupLaunch, launchProdJob]);

    const onRefuseValidationClick = useCallback(async () => {
        if (pack == null) return;

        await refusePack(pack.id, pack.validation.id);
        setRoute('measures');
    }, [pack, refusePack, setRoute]);

    const onApproveValidationClick = useCallback(async () => {
        if (pack == null) return;

        const success = await sendValidations();
        if (!success) return;

        await acceptPack(pack.id, pack.validation.id);
        setRoute('measures');
    }, [pack, acceptPack, sendValidations, setRoute]);

    const heading = useMemo<ProjectHeadingState>(
        () => ({
            title: <>
                {pack &&
                    <div className='flex flex-row gap-x-2 items-center'>
                        <Switch value={mode}>
                            <Case value={'thermal'}>
                                <>
                                    <FontAwesomeIcon icon={faTemperatureFull} className='text-2xl'/>
                                    <span className='font-semibold'>Chauffage</span>
                                </>
                            </Case>
                            <Case value={'functional'}>
                                <>
                                    <FontAwesomeIcon icon={faPuzzle} className='text-2xl'/>
                                    <span className='font-semibold'>Fonctionnel</span>
                                </>
                            </Case>
                        </Switch>

                        <div className='flex flex-row gap-x-1.5 items-center text-gray-900'>
                            <span>:</span>
                            <span>{pack.buildingName}</span>
                            <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                            <span>{pack.storeyName}</span></div>
                    </div>
                }
            </>,
            left:
            <>
                <Tooltip id='validation-toolbar-tooltip' delayShow={100} className='z-1'/>
                {pack &&
                    <div className='flex flex-row gap-x-2 items-center'>
                        <Button variant='simple' onClick={onBackClick}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Button>
                        
                        <Switch value={mode}>
                            <Case value={'thermal'}>
                                <Button onClick={onSaveAndUpdateClick}>
                                    Sauvegarder et mettre à jour
                                </Button>
                            </Case>
                            <Case value={'functional'}>
                                <>
                                    <ValidationBadge validation={pack?.validation?.state}
                                                     data-tooltip-id='validation-toolbar-tooltip'
                                                     data-tooltip-content='Etat actuel'
                                                     data-tooltip-place='bottom' />

                                    <ButtonLockDependent onClick={onRefuseValidationClick} variant='danger' size='sm'>
                                        <div className='flex flex-row gap-x-2 items-center'>
                                            <FontAwesomeIcon icon={faTriangleExclamation} />
                                            <span>Refuser</span>
                                        </div>
                                    </ButtonLockDependent>

                                    <ButtonLockDependent onClick={onApproveValidationClick} variant='success' size='sm'>
                                        <div className='flex flex-row gap-x-2 items-center'>
                                            <FontAwesomeIcon icon={faCheck} />
                                            <span>Valider</span>
                                        </div>
                                    </ButtonLockDependent>
                                </>
                            </Case>
                        </Switch>
                    </div>
                }
            </>
        }), [pack, mode, onBackClick, onSaveAndUpdateClick, onRefuseValidationClick, onApproveValidationClick]);

    useProjectFormHeadingLayoutEffect(heading);
    
    const [isFullScreen, setIsFullScreen] = useState(false);

    const loaders = useValidationLoaders(storeyId, pack?.id ?? '');
    const tools = useValidationTools();
    
    if (isLoading)
        return <SimpleLoadingIndicator/>;
    
    if (pack == null)
        return <></>;

    return (
        <ViewerContextProvider loaders={loaders} tools={tools}>
            <div className='relative h-full w-full flex-1 min-h-0'>
                
                <PackViewerContainer pack={pack} mode={mode}/>
                
                <div className='absolute top-4 left-4'>
                    <PackToolbar packId={pack.id} storeyId={storeyId}/>
                </div>

                <Card
                    additionalClasses={`absolute p-2 left-4 bottom-4 overflow-hidden transition-all duration-175 ease-in-out ${isFullScreen ? 'z-2 w-[calc(100%-2rem)] h-[calc(100%-2rem)]' : 'z-1 w-80 h-70'}`}>
                    <div className='relative h-full w-full'>
                        <Button variant='ghost' size='sm' className='absolute top-2 right-2 z-2'
                                onClick={() => setIsFullScreen(!isFullScreen)}>
                            <FontAwesomeIcon
                                icon={isFullScreen ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter}/>
                        </Button>
                        <PackDetailsSlide pack={pack} isFullScreen={isFullScreen}/>
                    </div>
                </Card>

                <div className='absolute right-4 bottom-4 z-1'>
                    <PackSelection mode={mode} pack={pack}/>
                </div>
            </div>
        </ViewerContextProvider>
    );
};

const ValidationDetails : React.FC<ValidationDetailsProps> = ({storeyId}) =>
    <ProjectMeasureProvider>
        <ValidationDetailsComponent storeyId={storeyId}/>
    </ProjectMeasureProvider>;

export default ValidationDetails;
